import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bank, Factory, Storefront, Heartbeat, Lightbulb } from "@phosphor-icons/react";
import { useMarketing } from "@/components/marketing/MarketingLayout";
import {
  NAVY, GOLD, GREEN, Reveal, Overline, SectionHeader, GlassCard, EditorialHero,
} from "@/components/marketing/editorial";

const FUNCTIONS = [
  { id: "close", label: "Close & Controllership", blurb: "Checklists, reconciliations, accruals and flux commentary — orchestrated end to end." },
  { id: "p2p", label: "Accounts Payable", blurb: "Invoice capture, 3-way matching, duplicate detection and payment controls." },
  { id: "o2c", label: "Accounts Receivable", blurb: "Cash application, collections prioritization, disputes and credit risk." },
  { id: "treasury", label: "Treasury & FP&A", blurb: "Cash positioning, rolling forecasts, variance drivers and scenario planning." },
  { id: "tax", label: "Tax & Compliance", blurb: "Tax code validation, regulatory reporting and continuous compliance monitoring." },
  { id: "reporting", label: "Reporting & Analytics", blurb: "Auto-generated financials, live KPI dashboards and natural-language queries." },
];

const INDUSTRIES = [
  { id: "banking", icon: Bank, label: "Banking & Financial Services",
    blurb: "High transaction volumes, regulatory reporting and strict audit trails make finance ops here unforgiving of manual error.",
    how: "Agents reconcile nostro/vostro and GL positions overnight, validate tax and compliance rules per transaction, and keep an evidence trail ready for examiners." },
  { id: "manufacturing", icon: Factory, label: "Manufacturing & Distribution",
    blurb: "Multi-entity consolidation, inventory-driven AP/AR and complex intercompany flows strain month-end close.",
    how: "Agents match goods-receipt to invoice automatically, net intercompany balances, and roll up multi-entity financials on a daily cadence instead of month-end." },
  { id: "retail", icon: Storefront, label: "Retail & Consumer",
    blurb: "High-volume, low-value transactions and seasonal swings make cash application and forecasting a constant scramble.",
    how: "Agents auto-match remittance-light payments, flex collections priority by season, and refresh the rolling forecast nightly against POS and payment data." },
  { id: "healthcare", icon: Heartbeat, label: "Healthcare & Life Sciences",
    blurb: "Payer reconciliations, grant/fund accounting and audit readiness demand continuous, not periodic, controls.",
    how: "Agents reconcile payer remittances line-by-line, track fund restrictions automatically, and keep a standing audit-readiness scorecard rather than a quarterly scramble." },
  { id: "tech", icon: Lightbulb, label: "Technology & Professional Services",
    blurb: "Recurring revenue, multi-currency consolidation and services-based billing create AR and reporting complexity.",
    how: "Agents validate revenue recognition against contract terms, consolidate multi-currency entities same-day, and answer ad-hoc finance questions in natural language." },
];

export default function SolutionsPage() {
  const { openDemo } = useMarketing();

  return (
    <>
      <EditorialHero
        eyebrow="Solutions · by function & by industry"
        title="Built for how"
        italic="your business actually runs."
        sub="Whether you're organized by finance function or by industry, Vaku's AI agents plug into your existing ERP and start executing from day one."
      />

      <section className="py-20 sm:py-28 border-b bg-white" style={{ borderColor: `${NAVY}10` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader eyebrow="By function" title="Every finance domain," italic="one agent layer." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FUNCTIONS.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.05}>
                <Link to={`/use-cases#${f.id}`} data-testid={`solutions-function-${f.id}`}>
                  <GlassCard className="h-full group">
                    <div className="font-serif text-xl font-light tracking-tight group-hover:text-[#C8A24B] transition-colors" style={{ color: NAVY }}>
                      {f.label}
                    </div>
                    <p className="mt-3 text-sm font-light leading-relaxed" style={{ color: `${NAVY}99` }}>{f.blurb}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: GOLD }}>
                      See the workflows <ArrowRight size={14} />
                    </div>
                  </GlassCard>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader eyebrow="By industry" title="Finance AI that fits" italic="your sector." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.id} delay={i * 0.05}>
                  <Link to={`/solutions/${ind.id}`} data-testid={`solutions-industry-${ind.id}`}>
                    <GlassCard className="h-full group">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 mb-4"
                           style={{ background: `${GOLD}1A` }}>
                        <Icon size={22} weight="duotone" style={{ color: GOLD }} />
                      </div>
                      <div className="font-serif text-xl font-light tracking-tight group-hover:text-[#C8A24B] transition-colors" style={{ color: NAVY }}>
                        {ind.label}
                      </div>
                      <p className="mt-3 text-sm font-light leading-relaxed" style={{ color: `${NAVY}99` }}>{ind.blurb}</p>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: GOLD }}>
                        Explore solution <ArrowRight size={14} />
                      </div>
                    </GlassCard>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 border-t" style={{ borderColor: `${NAVY}10`, background: NAVY }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-white">
            Not sure which solution fits? <span className="italic" style={{ color: GOLD }}>Let's talk.</span>
          </h2>
          <button onClick={openDemo} data-testid="solutions-book-demo-button"
            className="mt-8 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-colors shadow-lg"
            style={{ background: GREEN }}
            onMouseEnter={(e) => (e.currentTarget.style.background = GOLD)}
            onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}>
            Book a Demo
          </button>
        </div>
      </section>
    </>
  );
}
